import type { ReactNode } from 'react';

import { Tooltip } from '../tooltip/tooltip.component';
import { classNames } from '../utils/class-names';
import styles from './form-field.component.module.css';

export interface FormFieldProps {
	/** Field name — also the control's id, so the label's htmlFor connects. */
	name: string;
	label?: ReactNode;
	description?: ReactNode;
	/** Help text shown in a tooltip off a small info marker beside the label. */
	tooltip?: ReactNode;
	error?: string;
	required?: boolean;
	/** `inline` places the control before the label (checkbox / switch). */
	layout?: 'stacked' | 'inline';
	/**
	 * When set, the label renders as a plain element carrying this id (instead of
	 * a `<label htmlFor>`), so a group control (e.g. a radio group) can name itself
	 * via `aria-labelledby` — a `<label htmlFor>` can only target a single control.
	 */
	labelId?: string;
	className?: string;
	/** The control itself. */
	children: ReactNode;
}

/** The shared field frame: label (+ required marker / tooltip), description, control, error. */
export function FormField({
	name,
	label,
	description,
	tooltip,
	error,
	required,
	layout = 'stacked',
	labelId,
	className,
	children,
}: FormFieldProps) {
	const labelContent = (
		<>
			{label}
			{required && (
				<span className={styles.required} aria-hidden>
					*
				</span>
			)}
			{tooltip && (
				<Tooltip content={tooltip}>
					<span className={styles.hint} tabIndex={0} role="img" aria-label="More information">
						<svg viewBox="0 0 16 16" fill="none" aria-hidden focusable="false">
							<circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" />
							<path
								d="M8 7.2v4M8 5.2h.01"
								stroke="currentColor"
								strokeWidth="1.5"
								strokeLinecap="round"
							/>
						</svg>
					</span>
				</Tooltip>
			)}
		</>
	);
	let labelEl: ReactNode = null;
	if (label) {
		labelEl = labelId ? (
			<span id={labelId} className={styles.label}>
				{labelContent}
			</span>
		) : (
			<label htmlFor={name} className={styles.label}>
				{labelContent}
			</label>
		);
	}

	const descriptionEl = description ? <p className={styles.description}>{description}</p> : null;
	const errorEl = error ? (
		<p className={styles.error} role="alert">
			{error}
		</p>
	) : null;

	if (layout === 'inline') {
		return (
			<div className={classNames(styles.field, className)}>
				<div className={styles.inline}>
					{children}
					{labelEl}
				</div>
				{descriptionEl}
				{errorEl}
			</div>
		);
	}

	return (
		<div className={classNames(styles.field, className)}>
			{labelEl}
			{descriptionEl}
			{children}
			{errorEl}
		</div>
	);
}
